import { Link, useNavigate } from 'react-router';

import { useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { ImageCardCollection } from 'Components/ImageCardCollection';
import { Button } from 'Components/LegacyButton';
import { MetaData } from 'Components/MetaData';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { productsCategories, productsSlugs, productsSubCategories } from 'constants/products';

import BlockLayout from 'Blocks/BlockLayout';
import { Faq } from 'Blocks/Faq';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import { content } from 'assets/content/brushes_lp';

import { formatPriceWithCurrency } from 'utils/currencies';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import { getBrushProductPageContent, getProductPricesContentFromAPI } from 'dux/products/selectors';
import { useFetchProductsList, useTrackProductViewed } from 'dux/products/hooks';

const HeroSection = styled.section`
  position: relative;

  display: flex;
  width: 100%;
  height: 50vw;

  text-align: center;
  color: ${theme.colors.neutral[100]};

  ${legacyTheme.breakpoints.match('md')} {
    flex-direction: column;
    width: 100%;
    height: auto;
  }
`;

const HeroTitle = styled(Typography)`
  max-width: ${legacyTheme.width.half};

  font-size: 90px;

  ${legacyTheme.breakpoints.match('md')} {
    font-size: 60px;
  }

  ${legacyTheme.breakpoints.match('sm')} {
    font-size: 36px;
  }
`;

const HeroBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;

  background-size: cover;
  background-position: center center;

  ${legacyTheme.breakpoints.match('md')} {
    width: 100%;
    min-height: 50vw;
    padding: 0 ${legacyTheme.props.minimumBlockPadding};
  }

  ${legacyTheme.breakpoints.match('sm')} {
    width: 100%;
    min-height: 80vw;
  }
`;

const HeroBlock1 = styled(HeroBlock)`
  background-image: url(${content.hero.image1Desktop.src});

  ${legacyTheme.breakpoints.match('md')} {
    background-image: url(${content.hero.image1Tablet.src});
  }

  ${legacyTheme.breakpoints.match('sm')} {
    background-image: url(${content.hero.image1Mobile.src});
  }
`;

const HeroBlock2 = styled(HeroBlock)`
  background-image: url(${content.hero.image2Desktop.src});

  ${legacyTheme.breakpoints.match('md')} {
    background-image: url(${content.hero.image2Tablet.src});
  }

  ${legacyTheme.breakpoints.match('sm')} {
    background-image: url(${content.hero.image2Mobile.src});
  }
`;

const HeroEyebrow1 = styled(Typography)`
  position: absolute;
  top: ${legacyTheme.spacing.s48};

  width: 100%;

  ${legacyTheme.breakpoints.match('md')} {
    top: ${legacyTheme.spacing.s36};
  }

  ${legacyTheme.breakpoints.match('sm')} {
    top: ${legacyTheme.spacing.s24};
  }
`;

const HeroEyebrow2 = styled(Typography)`
  position: absolute;
  bottom: ${legacyTheme.spacing.s48};

  width: 100%;

  ${legacyTheme.breakpoints.match('md')} {
    bottom: ${legacyTheme.spacing.s36};
  }

  ${legacyTheme.breakpoints.match('sm')} {
    bottom: ${legacyTheme.spacing.s24};
  }
`;

const IntroSection = styled.section`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 50vw;
  padding: 0 0 0 50%;

  background-image: url(${content.intro.img.imageDesktop.src});
  background-color: ${theme.colors.neutral['500']};
  background-position: left center;
  background-repeat: no-repeat;
  background-size: 50% 100%;

  ${legacyTheme.breakpoints.match('md')} {
    display: block;

    min-height: auto;
    padding: 0 ${legacyTheme.props.minimumBlockPadding};
    padding-bottom: 75%;

    background-image: url(${content.intro.img.imageTablet.src});
    background-position: bottom center;
    background-size: 100% auto;
  }

  ${legacyTheme.breakpoints.match('sm')} {
    padding-bottom: 100%;
    background-image: url(${content.intro.img.imageMobile.src});
  }
`;

const IntroContent = styled.div`
  max-width: ${legacyTheme.width.half};
  margin: ${legacyTheme.props.verticalSpacingMob} auto;

  color: ${theme.colors.neutral[100]};

  ${legacyTheme.breakpoints.match('md')} {
    display: flex;
    flex-direction: column;
    align-items: center;

    max-width: ${legacyTheme.width.normal};

    text-align: center;
  }
`;

const CollectionTitle = styled(Typography)`
  color: ${theme.colors.primary[400]};
`;

const HandsOnSection = styled.section`
  width: 100%;
  /* WARN: Exceptionally let a hexcode color for the sake of visual coherence with the section's images background */
  /* background-color: ${theme.colors.primary['400']}; */
  background-color: #344143;

  text-align: center;
  color: ${theme.colors.neutral[100]};
`;

const HandsOnContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  max-width: ${legacyTheme.width.veryWide};
  margin: auto;
  padding: 0 ${legacyTheme.props.minimumBlockPadding};

  ${legacyTheme.breakpoints.match('sm')} {
    width: 100%;
  }
`;

const HandsOnImage = styled(Image)`
  display: block;
  width: 100%;
  height: auto;
`;

const ArmTopSection = styled.section`
  width: 100%;
  padding: 0 ${legacyTheme.props.minimumBlockPadding};

  background-color: ${theme.colors.neutral['500']};
`;

const ArmSection = styled.section`
  width: 100%;
  padding: 0 ${legacyTheme.props.minimumBlockPadding};
  padding-top: 700px;
  /* WARN: Exceptionally let a hexcode color for the sake of visual coherence with the section's images background */
  /* background-color: ${theme.colors.neutral['500']}; */
  background-color: #b1a49c;
  background-image: url(${content.arm.imageDesktop.src});
  background-position: center top;
  background-repeat: no-repeat;
  background-size: 1440px;

  text-align: center;
  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.match('md')} {
    padding-top: 120%;
    background-image: url(${content.arm.imageTablet.src});
    background-size: 100%;
  }

  ${legacyTheme.breakpoints.match('sm')} {
    padding-top: 180%;
    background-image: url(${content.arm.imageMobile.src});
  }

  ${legacyTheme.breakpoints.match('xs')} {
    padding-top: 150%;
    background-image: url(${content.arm.imageMobile.src});
  }
`;

const ArmContainer = styled.div<{ nomargin?: boolean }>`
  position: relative;

  max-width: ${legacyTheme.width.wide};
  margin: ${legacyTheme.props.verticalSpacingDes} auto;

  text-align: center;
  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.match('md')} {
    margin: ${legacyTheme.props.verticalSpacingMob} auto;
  }

  ${legacyTheme.breakpoints.match('sm')} {
    max-width: ${legacyTheme.width.half};
  }

  margin-bottom: ${({ nomargin }) => (nomargin ? '0' : 'initial')};
`;

const ArmTitle = styled(Typography)`
  margin-bottom: ${legacyTheme.spacing.s24};
  margin: 0;

  font-size: 150px;

  ${legacyTheme.breakpoints.match('md')} {
    font-size: 70px;
  }

  ${legacyTheme.breakpoints.match('sm')} {
    font-size: 57px;
  }
`;

const ArmSubtitle = styled(Typography)`
  margin: 0;
  margin-bottom: 0.5em;

  ${legacyTheme.breakpoints.match('md')} {
    font-size: 16px;
  }
`;

const ArmContent = styled(Typography)`
  margin: 0;
  margin-bottom: 0.5em;

  ${legacyTheme.breakpoints.match('md')} {
    font-size: 12px;
  }
`;

const ArmBullets = styled.div`
  & div {
    position: absolute;
    width: 300px;
  }

  & div:nth-of-type(1) {
    top: 380px;
    left: 40px;
    text-align: right;
  }

  & div:nth-of-type(2) {
    top: 535px;
    right: -25px;
    text-align: left;
  }

  & div:nth-of-type(3) {
    top: 700px;
    left: 40px;
    text-align: right;
  }

  & div:nth-of-type(4) {
    top: 915px;
    right: -25px;
    text-align: left;
  }

  ${legacyTheme.breakpoints.match('md')} {
    display: grid;
    grid-gap: ${legacyTheme.spacing.s24} ${legacyTheme.spacing.s24};
    grid-template-columns: 1fr 1fr 1fr 1fr;

    & div {
      position: static;
      width: auto;
      text-align: center;
    }

    & div:nth-of-type(1) {
      text-align: center;
    }

    & div:nth-of-type(2) {
      text-align: center;
    }

    & div:nth-of-type(3) {
      text-align: center;
    }

    & div:nth-of-type(4) {
      text-align: center;
    }
  }

  ${legacyTheme.breakpoints.match('sm')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const ArmImagesContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${legacyTheme.spacing.s24};
`;

const ArmImageWrapper = styled.div`
  flex-grow: 1;
  width: 100px;
  margin-right: ${legacyTheme.spacing.s24};

  &:last-child {
    margin-right: 0;
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
`;

const StickyButton = styled(Button)`
  position: sticky;
  bottom: 0;
  width: 100%;
  margin: 0;
`;

type BrushesLandingProps = {
  footer?: boolean;
  header?: boolean;
};

const BrushesLanding = ({ header = true, footer = true }: BrushesLandingProps) => {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsiveVariant();

  const brushContent = useAppSelector(getBrushProductPageContent);

  const contentFromAPI = useAppSelector((state) =>
    getProductPricesContentFromAPI(state, { productSlug: productsSlugs.BRUSH }),
  );

  const herkunftImages = isMobile ? content.arm.herkunftMobile : content.arm.herkunftDesktop;

  useFetchProductsList();
  useTrackProductViewed({
    category: productsCategories.OTHERS,
    subcategory: productsSubCategories.TOOLS,
    product: brushContent?.heroModule.product,
  });

  return (
    <>
      <MetaData
        description="Find your perfect hair brush—boar bristle, boar + nylon or tipped-metal bristle—and add a personalized monogram to make it yours. Shop Prose hair brushes."
        title="Hair Brushes - Hair Care | Prose"
      />
      <BaseScene background="beige" footer={footer} header={header}>
        <HeroSection>
          <HeroEyebrow1 markupName="p" variant={isMobile ? 'mono3' : 'mono1'}>
            {content.hero.eyebrow1}
          </HeroEyebrow1>

          <HeroBlock1>
            <HeroTitle markupName="h1" variant="h1">
              {content.hero.headline1}
            </HeroTitle>
          </HeroBlock1>

          <HeroBlock2>
            <HeroTitle markupName="h1" variant="h1">
              {content.hero.headline2}
            </HeroTitle>
          </HeroBlock2>

          <HeroEyebrow2 markupName="p" variant={isMobile ? 'mono3' : 'mono1'}>
            {content.hero.eyebrow2}
          </HeroEyebrow2>
        </HeroSection>

        <IntroSection>
          <IntroContent>
            <Typography color="noirDark" paragraph variant="h1">
              {content.intro.headline}
            </Typography>

            <Typography color="noirDark" paragraph variant="p2">
              {content.intro.body}
            </Typography>

            {!isMobile && (
              <Button
                Component={Link}
                data-click="shop-your-brushes"
                data-from="intro-block"
                marginTop
                to="/products/brushes"
                variant="noir"
              >
                {content.intro.cta}
              </Button>
            )}
          </IntroContent>
        </IntroSection>

        {/* @ts-expect-error BlockLayout is not typed yed  */}
        <BlockLayout
          backgroundColor={theme.colors.tertiary['300']}
          contentAlign="center"
          contentWidth="veryWide"
        >
          <CollectionTitle paragraph variant="mono1">
            {content.collection.headline}
          </CollectionTitle>

          <ImageCardCollection
            list={content.collection.brushes(
              contentFromAPI &&
                formatPriceWithCurrency({
                  price: contentFromAPI.price,
                  currency: contentFromAPI.currency,
                }),
            )}
            onClick={() => navigate('/products/brushes')}
          />

          {!isMobile && (
            <Button
              Component={Link}
              data-click="shop-your-brushes"
              data-from="collection-block"
              marginTop
              to="/products/brushes"
              variant="noir"
            >
              {content.intro.cta}
            </Button>
          )}
        </BlockLayout>

        <HandsOnSection>
          <HandsOnImage
            alt={content.handsOn.imageTopAlt}
            src={
              isMobile
                ? content.handsOn.imageTopMobile
                : isTablet
                ? content.handsOn.imageTopTablet
                : content.handsOn.imageTopDesktop
            }
          />

          <HandsOnContent>
            <Typography paragraph variant="h2r">
              {content.handsOn.body}
            </Typography>
            <Button
              Component="a"
              data-click="blog-article"
              data-from="hands-on-block"
              href={content.handsOn.ctaArticleUrl}
              marginTop
              variant="white"
            >
              {content.handsOn.cta}
            </Button>
          </HandsOnContent>

          <HandsOnImage
            alt={content.handsOn.imageBottomAlt}
            src={
              isMobile
                ? content.handsOn.imageBottomMobile
                : isTablet
                ? content.handsOn.imageBottomTablet
                : content.handsOn.imageBottomDesktop
            }
          />
        </HandsOnSection>

        <ArmTopSection>
          <ArmContainer nomargin>
            <ArmTitle markupName="h1" variant="h1">
              {content.arm.title}
            </ArmTitle>

            <ArmBullets>
              {content.arm.bullets.map(({ title, content: bulletContent }, i) => (
                <div key={i}>
                  <ArmSubtitle markupName="h2" variant="h3">
                    {title}
                  </ArmSubtitle>
                  <ArmContent markupName="p" variant="p2">
                    {bulletContent}
                  </ArmContent>
                </div>
              ))}
            </ArmBullets>
          </ArmContainer>
        </ArmTopSection>

        <ArmSection>
          <ArmContainer>
            <ArmTitle markupName="h1" variant="h1">
              {content.arm.titleBis}
            </ArmTitle>

            <ArmImagesContainer>
              {herkunftImages.map(({ alt, image }) => (
                <ArmImageWrapper key={image.src}>
                  <StyledImage alt={alt} src={image} />
                </ArmImageWrapper>
              ))}
            </ArmImagesContainer>

            <Typography paragraph variant="p2">
              {content.arm.descriptionBis}
            </Typography>

            <Button
              Component="a"
              data-click="blog-article"
              data-from="arm-block"
              href={content.arm.ctaArticleUrl}
              marginTop
              variant="noir"
            >
              {content.arm.cta}
            </Button>
            <Spacer size={20} />
          </ArmContainer>
        </ArmSection>

        {isMobile && (
          <StickyButton
            Component={Link}
            data-click="shop-your-brushes"
            data-from="sticky-cta"
            to="/products/brushes"
            variant="vert"
          >
            {content.intro.cta}
          </StickyButton>
        )}

        <Faq
          backgroundColor={theme.colors.primary[400]}
          questions={content.faq.questions}
          title={content.faq.title}
          variant="dark"
        />
      </BaseScene>
    </>
  );
};

export default BrushesLanding;
